import React from 'react'
import { tv } from 'tailwind-variants'

const segment = tv({
  base: 'flex-1 text-center cursor-pointer select-none text-sm font-bold rounded-md py-[7px] px-2.5',
  variants: {
    active: {
      true: 'bg-surface text-body shadow-sm',
      false: 'text-muted'
    }
  }
})

interface SegmentedControlProps<T extends string> {
  title: string
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (value: T) => void
  ariaLabel?: string
  // The pane for the selected option, shown beneath the switcher.
  children: React.ReactNode
}

// A titled panel section whose content switches between mutually exclusive modes: one pill per option in
// a grey track, the active one raised, with the chosen mode's pane below. Used where a dropdown would be
// heavier than the choice deserves — e.g. whether a select column's options are a manual list or sourced
// from a table, in both the add-column configure stage and the Edit Column menu.
const SegmentedControl = <T extends string>({
  title,
  options,
  value,
  onChange,
  ariaLabel,
  children
}: SegmentedControlProps<T>): React.ReactElement => (
  <div className="mt-4 border-t border-divider">
    <div className="bg-surface-sunken p-2.5">
      <span className="font-bold text-sm">{title}</span>
    </div>
    <div className="px-2.5 pt-4">
      <div
        role="group"
        aria-label={ariaLabel}
        className="flex w-full bg-surface-sunken border border-divider rounded-lg p-[3px] gap-[2px]"
      >
        {options.map((option) => (
          <div
            key={option.value}
            role="button"
            tabIndex={0}
            aria-pressed={value === option.value}
            className={segment({ active: value === option.value })}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              // Stop Space scrolling the menu; both keys select the focused segment.
              event.preventDefault()
              onChange(option.value)
            }}
          >
            {option.label}
          </div>
        ))}
      </div>
    </div>
    {children}
  </div>
)

export default SegmentedControl
